import type { SportType } from '@/types';

export const LOCAL_APP_STATE_KEY = 'skore.app-state.v1';
export const LOCAL_APP_STATE_VERSION = 1;

export type OnboardingStatus = 'new' | 'skipped' | 'complete';

export interface ReplayPlanItem {
  id: string;
  sportType: SportType;
  utcDatetime: string;
  homeTeamName: string;
  awayTeamName: string;
  competition?: string;
  arena?: string;
  intent: 'watch-later';
  savedAt: string;
  reminderId?: number;
  reminderAt?: string;
}

export interface LocalAppState {
  version: typeof LOCAL_APP_STATE_VERSION;
  onboardingStatus: OnboardingStatus;
  preferredSports: SportType[];
  favoriteTeamIds: string[];
  replayPlan: ReplayPlanItem[];
}

export const emptyLocalAppState = (): LocalAppState => ({
  version: LOCAL_APP_STATE_VERSION,
  onboardingStatus: 'new',
  preferredSports: [],
  favoriteTeamIds: [],
  replayPlan: [],
});

const SPORTS = new Set<SportType>(['nba', 'soccer', 'nfl', 'mlb', 'nhl']);
const FORBIDDEN_PERSISTED_KEYS = new Set([
  'homeScore',
  'awayScore',
  'winner',
  'result',
  'gameRecapURL',
  'recapHeadline',
  'excitementScore',
  'pointsDifference',
  'totalScore',
]);

function isSafePlanItem(value: unknown): value is ReplayPlanItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  if (Object.keys(item).some((key) => FORBIDDEN_PERSISTED_KEYS.has(key))) return false;
  return typeof item.id === 'string'
    && SPORTS.has(item.sportType as SportType)
    && typeof item.utcDatetime === 'string'
    && !Number.isNaN(Date.parse(item.utcDatetime))
    && typeof item.homeTeamName === 'string'
    && typeof item.awayTeamName === 'string'
    && item.intent === 'watch-later'
    && typeof item.savedAt === 'string';
}

export function parseLocalAppState(serialized: string | null): LocalAppState {
  if (!serialized) return emptyLocalAppState();

  try {
    const value = JSON.parse(serialized) as Record<string, unknown>;
    if (value.version !== LOCAL_APP_STATE_VERSION) return emptyLocalAppState();

    const onboardingStatus = value.onboardingStatus;
    const preferredSports = Array.isArray(value.preferredSports)
      ? value.preferredSports.filter((sport): sport is SportType => SPORTS.has(sport as SportType))
      : [];
    const favoriteTeamIds = Array.isArray(value.favoriteTeamIds)
      ? value.favoriteTeamIds.filter((id): id is string => typeof id === 'string')
      : [];
    const replayPlan = Array.isArray(value.replayPlan)
      ? value.replayPlan.filter(isSafePlanItem)
      : [];

    return {
      version: LOCAL_APP_STATE_VERSION,
      onboardingStatus: onboardingStatus === 'complete' || onboardingStatus === 'skipped' ? onboardingStatus : 'new',
      preferredSports,
      favoriteTeamIds,
      replayPlan,
    };
  } catch {
    return emptyLocalAppState();
  }
}

export function serializeLocalAppState(state: LocalAppState): string {
  const safeState = parseLocalAppState(JSON.stringify(state));
  return JSON.stringify(safeState);
}

